// How a `/dashboard/...` path splits into the three segments the loaders take.
//
// Mirrors `dashboardRoute` in apps/pictufy/src/views/data/dashboard/index.ts, which is the
// authority — that is where the segments are consumed. The duplication is deliberate and
// small: the client needs to parse a URL it is about to navigate to, before any request
// goes out, and a round trip to ask the server how to read its own URL would be absurd.
// Keep the two in step; there is nothing else in either file.
//
// Mind the pseudo-tabs. `include-hidden` is not a tab — it loads the resellers tab with
// hidden rows — and `weekly` / `monthly` / `yearly` all load overview with a different
// `periodMode`. Both are why `tab` is a plain string rather than a union here.

export interface DashboardRoute {
  /** `$da[2]`, defaulted. */
  tab: string;
  /** `$da[3]` — sub-tab, employee filter, or "". */
  subtab: string;
  /** `$da[4]` — artist filter, sort flag, or "". */
  filter: string;
}

export function dashboardRoute(pathname: string): DashboardRoute {
  const da = pathname.split("/");
  return { tab: da[2] || "overview", subtab: da[3] ?? "", filter: da[4] ?? "" };
}

/**
 * The Artworks tab's four sub-tabs.
 *
 * Here rather than in `artworks.tsx` so that a module wanting only to LINK to a sub-tab —
 * the Artists tab's "Blocked" column, say — does not have to import the whole Artworks
 * view. That import was real and it broke two test files: `artworks.tsx` reaches
 * `lib/constants` and `ui/modal`, which touch `window` at module scope, so importing it
 * from `tables.tsx` made every node-environment test that loaded the table blow up.
 * These two functions are pure string work and belong next to `dashboardRoute`.
 */
export type ArtworksTab = "incoming" | "hidden" | "force-blocked" | "exclusive";

const ARTWORKS_TABS: readonly ArtworksTab[] = ["incoming", "hidden", "force-blocked", "exclusive"];

/**
 * Which sub-tab a `/dashboard/artworks/...` URL names, and how to build one.
 *
 * The sub-tab used to be component state, so no view here could be linked or shared — the
 * reason given was that `subtab` (`$da[3]`) is already the artist filter and a second
 * meaning would collide with it. It does not have to: an artist filter is a number and a
 * sub-tab name never is, so one segment can carry either unambiguously.
 *
 *   /dashboard/artworks                     → Incoming, every artist
 *   /dashboard/artworks/exclusive           → Exclusive, every artist
 *   /dashboard/artworks/823348              → Incoming, that artist      (unchanged)
 *   /dashboard/artworks/823348/exclusive    → Exclusive, that artist
 *
 * The third form is what the artist profile's admin menu already emits, and it keeps
 * working untouched — which is the whole reason for discriminating on shape rather than
 * moving the artist id to another segment.
 *
 * Incoming is left OUT of the URL rather than spelled in it, so the default view has one
 * canonical address instead of two that render the same thing.
 */
export function artworksRoute(pathname: string): {
  artistId: number | null;
  tab: ArtworksTab;
} {
  const { subtab, filter } = dashboardRoute(pathname);
  const artistId = /^\d+$/.test(subtab) && Number(subtab) > 0 ? Number(subtab) : null;
  const tabSeg = artistId === null ? subtab : filter;
  const tab = ARTWORKS_TABS.find((t) => t === tabSeg) ?? "incoming";
  return { artistId, tab };
}

/** The URL for one sub-tab, keeping whatever artist filter is in effect. */
export function artworksHref(artistId: number | null, tab: ArtworksTab): string {
  const segments = ["/dashboard/artworks"];
  if (artistId !== null) segments.push(String(artistId));
  if (tab !== "incoming") segments.push(tab);
  return segments.join("/");
}
